/**
 * firebase.js
 * Firebase client SDK initialization + FCM token management for web.
 *
 * What this file does:
 *  1. Initializes the Firebase app with the project config
 *  2. Exports a helper to request notification permission and get the FCM token
 *  3. Exports a helper to call the backend to register/unregister the token
 *
 * What it does NOT do:
 *  - It does not send notifications (that's the backend/cron job's job)
 *  - It does not need the service account JSON (that's backend-only)
 */

import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import axiosInstance, { endpoints } from 'src/utils/axios';

// ── Firebase client-side config ───────────────────────────────────────────────
// This is safe to be in frontend code — it only identifies your Firebase project.
// It does NOT give anyone admin access to your Firebase project.
const firebaseConfig = {
  apiKey: 'AIzaSyCpYhnmN3nD_deGLU-_zImWjqA6sYw3xi0',
  authDomain: 'fieldhrm-app.firebaseapp.com',
  projectId: 'fieldhrm-app',
  storageBucket: 'fieldhrm-app.firebasestorage.app',
  messagingSenderId: '214698877590',
  appId: '1:214698877590:web:db652214dacc9d9cb718b7',
};

// Initialize Firebase only once (React StrictMode / HMR safe)
const firebaseApp = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

// Lazy-initialized messaging instance
// (module-level getMessaging() can fail before the browser context is fully ready)
let _messaging = null;

function getFirebaseMessaging() {
  if (_messaging) return _messaging;
  try {
    _messaging = getMessaging(firebaseApp);
    return _messaging;
  } catch (err) {
    console.warn('[FCM] Firebase Messaging init failed:', err.message);
    return null;
  }
}

// ── VAPID Key ─────────────────────────────────────────────────────────────────
// Generate this from: Firebase Console → Project Settings → Cloud Messaging → Web Push Certificates
// Paste the "Key pair" value below. Your senior needs to create this.
// Leave as null until the VAPID key is available — token generation will be skipped gracefully.
const VAPID_KEY = "BGUp2-t2RDgK0Ie7eacHNMIItLNkd8Kd7wD8E029xAeZa_btxab4JzUECTguBdtpnjv044Vq26GAA_1zCludQkY"; // TODO: Replace with your VAPID key from Firebase Console

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Request notification permission from the browser and get the FCM token.
 * Returns the FCM token string, or null if permission denied / not supported.
 *
 * Call this after the user logs in successfully.
 */
export async function requestNotificationPermission() {
  console.group('[FCM] Attempting to get push notification token...');

  // Step 1: Check if Firebase Messaging is available in this browser
  const messaging = getFirebaseMessaging();
  if (!messaging) {
    console.error('[FCM] ❌ Step 1 FAILED — Firebase Messaging is not supported in this browser.');
    console.groupEnd();
    return null;
  }
  console.info('[FCM] ✅ Step 1 — Firebase Messaging ready');

  // Step 2: Check VAPID key
  if (!VAPID_KEY) {
    console.error(
      `[FCM] ❌ Step 2 FAILED — VAPID key is missing!\n\n📋 How to fix:\n  1. Firebase Console → https://console.firebase.google.com\n  2. Project: fieldhrm-app\n  3. Project Settings → Cloud Messaging → Web Push Certificates\n  4. Generate key pair → copy the value\n  5. Paste into firebase.js: const VAPID_KEY = "YOUR_KEY_HERE"\n\n⚠️  FCM tokens will NOT be generated until this is done.`
    );
    console.groupEnd();
    return null;
  }
  console.info('[FCM] ✅ Step 2 — VAPID key is set');

  // Step 3: Request browser notification permission
  try {
    // Check if it's already denied before asking
    if (Notification.permission === 'denied') {
      console.warn(
        '[FCM] ⚠️ Step 3 — Notifications are BLOCKED in browser settings.\n\n' +
        '📋 How to fix:\n' +
        '  1. Click the Lock 🔒 icon in the address bar.\n' +
        '  2. Toggle Notifications to "Allow" or click "Reset permission".\n' +
        '  3. Refresh the page.'
      );
      console.groupEnd();
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn('[FCM] ❌ Step 3 FAILED — Permission denied by user. Status:', permission);
      console.groupEnd();
      return null;
    }
    console.info('[FCM] ✅ Step 3 — Notification permission granted');
  } catch (err) {
    console.error('[FCM] ❌ Step 3 FAILED — Notification.requestPermission() error:', err.message);
    console.groupEnd();
    return null;
  }

  // Step 4: Register service worker + get token
  try {
    console.info('[FCM] Step 4 — Registering service worker...');
    const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    
    // Wait for the service worker to be active
    console.info('[FCM] Step 4 — Waiting for Service Worker to activate...');
    await navigator.serviceWorker.ready;

    console.info('[FCM] Step 4 — Requesting FCM token...');
    const token = await getToken(messaging, { 
      vapidKey: VAPID_KEY, 
      serviceWorkerRegistration: swReg 
    });

    if (token) {
      console.info('[FCM] ✅ Step 4 — Token obtained:', `${token.substring(0, 20)}...`);
      console.groupEnd();
      return token;
    }

    console.warn('[FCM] ❌ Step 4 — getToken() returned empty. Check Firebase console app config.');
    console.groupEnd();
    return null;
  } catch (err) {
    console.error('[FCM] ❌ Step 4 FAILED:', err.message);
    console.groupEnd();
    return null;
  }
}

/**
 * Register the FCM token with the backend (store in DeviceToken collection).
 * Call this right after requestNotificationPermission() returns a token.
 *
 * @param {string} fcmToken - the token returned by requestNotificationPermission()
 */
export async function registerFcmTokenWithBackend(fcmToken) {
  if (!fcmToken) return;

  try {
    // Parse a simple device name from the user agent
    const deviceName = getDeviceName();

    await axiosInstance.post(endpoints.chat.fcmToken, {
      fcmToken,
      deviceType: 'web',
      deviceName,
    });

    // Store locally so we can unregister on logout
    localStorage.setItem('fcmToken', fcmToken);

    console.info('FCM: Token registered with backend ✅');
  } catch (err) {
    // Non-critical — don't block the login flow
    console.error('FCM: Failed to register token with backend:', err);
  }
}

/**
 * Unregister the FCM token on logout (removes it from the backend DB).
 * Call this inside the logout flow BEFORE clearing localStorage.
 */
export async function unregisterFcmToken() {
  const fcmToken = localStorage.getItem('fcmToken');
  if (!fcmToken) return;

  try {
    await axiosInstance.delete(endpoints.chat.fcmToken, { data: { fcmToken } });
    localStorage.removeItem('fcmToken');
    console.info('FCM: Token unregistered ✅');
  } catch (err) {
    // Non-critical — token will be auto-cleaned by cron job if push fails
    console.error('FCM: Failed to unregister token:', err);
    localStorage.removeItem('fcmToken'); // Remove locally regardless
  }
}

/**
 * Listen for foreground messages (app is open in browser tab).
 * Firebase doesn't show a notification automatically when the app is in the foreground —
 * you need to handle it manually (e.g. show a snackbar/toast).
 *
 * @param {function} callback - called with the message payload: { notification: { title, body }, data }
 */
export function onForegroundMessage(callback) {
  const messaging = getFirebaseMessaging();
  if (!messaging) return () => { };
  return onMessage(messaging, callback);
}

// ── Utility ───────────────────────────────────────────────────────────────────

/**
 * Parse a simple device label from the browser user agent.
 * Just for human-readable identification in the admin panel.
 */
function getDeviceName() {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'Safari on iOS';
  if (/Android/.test(ua)) return 'Browser on Android';
  if (/Chrome/.test(ua)) return 'Chrome on Desktop';
  if (/Firefox/.test(ua)) return 'Firefox on Desktop';
  if (/Safari/.test(ua)) return 'Safari on Desktop';
  return 'Web Browser';
}

export default firebaseApp;
